import { useMemo } from 'react';
import { addDays, isFuture } from 'date-fns';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';

import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';

import { useResource } from '@atlassian/react-resource-router';

import { standardTrialTransactionAccountStatusResource } from '@atlassian/jira-router-resources-standard-trial-transaction-account-status/src/services/index.tsx';
import type {
	TransactionAccountData,
	TransactionAccountStatuses,
} from '@atlassian/jira-router-resources-standard-trial-transaction-account-status/src/services/types.tsx';

import { expValEquals } from '@atlassian/jira-feature-experiments';

import type { Product } from '@atlassian/auto-upgrade-not-a-trial';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { STANDARD_TRIAL_DAYS_TO_DEACTIVATION } from '../../constants.tsx';
import { useProductAndEdition } from '../use-product-and-edition/index.tsx';
import { useAutoUpgradeTrait } from '../use-auto-upgrade-trait/index.tsx';

export type AutoUpgradeEligibility = {
	isEnabled: boolean;
	deactivationDate?: Date;
	product?: Product;
	projectType?: ProjectType;
	trialEndDate?: Date;
};

export const useEnabledCriteria = (): AutoUpgradeEligibility => {
	const { product, productKey, projectType, isStandardEdition } = useProductAndEdition();

	const isSiteAdmin = useIsSiteAdmin();
	const productEntitlementDetails = useProductEntitlementDetails();
	const entitlementDetails =
		productKey && productEntitlementDetails && productEntitlementDetails[productKey];

	const billingSourceSystem = entitlementDetails?.billingSourceSystem || 'HAMS';
	const trialEndTime = entitlementDetails?.trialEndTime;
	const firstPredunningEndTime = entitlementDetails?.firstPredunningEndTime;

	const deactivationDate = useMemo(() => {
		if (!trialEndTime && !firstPredunningEndTime) return;

		// use firstPredunningEndTime if it's in the future since this will not be removed once the trial ends
		if (firstPredunningEndTime && isFuture(firstPredunningEndTime)) {
			return new Date(firstPredunningEndTime);
		}

		// use trialEndTime if it exists as a backup
		if (trialEndTime) {
			return addDays(new Date(trialEndTime), STANDARD_TRIAL_DAYS_TO_DEACTIVATION);
		}
	}, [trialEndTime, firstPredunningEndTime]);

	const trialEndDate = useMemo(
		() => (trialEndTime ? new Date(trialEndTime) : undefined),
		[trialEndTime],
	);

	const isInTrialOrPreDunning =
		entitlementDetails && !!deactivationDate && isFuture(deactivationDate);

	const {
		data: productTrait,
		loading: loadingTrait,
		error: errorTrait,
	} = useAutoUpgradeTrait({ projectType });

	const {
		data: transactionData,
		loading: loadingTransaction,
		error: errorTransaction,
	} = useResource<TransactionAccountStatuses>(standardTrialTransactionAccountStatusResource);

	const loading = loadingTrait || loadingTransaction;
	const error = errorTrait || errorTransaction;

	// only display the banner if NO billToPresent is available on any of the transaction accounts
	const isBillToPresent =
		transactionData &&
		transactionData?.transactionAccountDetails &&
		transactionData?.transactionAccountDetails.some(
			(account: TransactionAccountData) => account.billToPresent === true,
		);

	// all criteria to show the banner
	const isEnabled = Boolean(
		isStandardEdition &&
			product &&
			isSiteAdmin &&
			billingSourceSystem === 'CCP' &&
			isInTrialOrPreDunning &&
			productTrait?.value &&
			!error &&
			!loading &&
			!isBillToPresent &&
			expValEquals('auto_upgrade_not_a_trial', 'cohort', 'treatment', 'not-enrolled'),
	);

	return useMemo(
		() => ({
			isEnabled,
			deactivationDate,
			product,
			projectType,
			trialEndDate,
		}),
		[isEnabled, deactivationDate, product, trialEndDate, projectType],
	);
};
